import axiosInstance from 'shared/services/api/axiosInstance';
import { isDemoTeamActive } from 'shared/utils/demoTeam';

/**
 * Product Analytics API Client
 *
 * Provides methods to interact with the Product Analytics Platform backend.
 */

// Demo workspace = viewer-only. Existing boards/cards load and their queries run
// (execute is allow-listed server-side), but create/edit/delete are kept in the
// browser only — never saved — so users can play without errors. Not persisted.
let _demoBoardSeq = 0;
const _demoId = (p) => `demo-${p}-${(_demoBoardSeq += 1)}`;
const _demoNow = () => new Date().toISOString();

// ============================================================================
// BOARDS API
// ============================================================================

/**
 * Fetch pinned board IDs for the current user + team
 */
export const fetchPinnedBoards = async () => {
  const response = await axiosInstance.get('/boards/pinned');
  return response.data;
};

/**
 * Update pinned board IDs for the current user + team
 */
export const updatePinnedBoards = async (boardIds) => {
  if (isDemoTeamActive()) return { success: true, boardIds };
  const response = await axiosInstance.put('/boards/pinned', { boardIds });
  return response.data;
};

/**
 * Fetch all boards for the current team
 */
export const fetchBoards = async (params = {}) => {
  const { limit, offset, teamId } = params;
  const queryParams = new URLSearchParams();
  if (teamId != null && teamId !== '') queryParams.append('teamId', String(teamId));
  if (limit) queryParams.append('limit', limit);
  if (offset) queryParams.append('offset', offset);
  const response = await axiosInstance.get(`/boards?${queryParams.toString()}`);
  return response.data;
};

/**
 * Fetch single board by ID
 * @param {string|number} boardId
 * @param {boolean} [includeCards=true]
 * @param {string|number} [teamId] - optional team context when viewing a non-default team
 */
export const fetchBoard = async (boardId, includeCards = true, teamId = null) => {
  const params = new URLSearchParams({ includeCards });
  if (teamId != null && teamId !== '') params.append('teamId', String(teamId));
  const response = await axiosInstance.get(`/boards/${boardId}?${params.toString()}`);
  return response.data;
};

/**
 * Create new board
 */
export const createBoard = async (boardData, teamId = null) => {
  if (isDemoTeamActive()) {
    const id = _demoId('board');
    return { success: true, board: { id, ...boardData, cards: [], created_at: _demoNow(), updated_at: _demoNow(), _demo: true } };
  }
  const body = teamId != null && teamId !== '' ? { ...boardData, teamId } : boardData;
  const response = await axiosInstance.post('/boards', body);
  return response.data;
};

/**
 * Update board
 */
export const updateBoard = async (boardId, updates, teamId = null) => {
  if (isDemoTeamActive()) return { success: true, board: { id: boardId, ...updates, updated_at: _demoNow(), _demo: true } };
  const url = teamId != null && teamId !== '' ? `/boards/${boardId}?teamId=${encodeURIComponent(teamId)}` : `/boards/${boardId}`;
  const response = await axiosInstance.put(url, updates);
  return response.data;
};

/**
 * Delete board
 */
export const deleteBoard = async (boardId, teamId = null) => {
  if (isDemoTeamActive()) return { success: true };
  const url = teamId != null && teamId !== '' ? `/boards/${boardId}?teamId=${encodeURIComponent(teamId)}` : `/boards/${boardId}`;
  const response = await axiosInstance.delete(url);
  return response.data;
};

/**
 * Duplicate board
 */
export const duplicateBoard = async (boardId, newName = null, teamId = null) => {
  if (isDemoTeamActive()) return { success: true, board: { id: _demoId('board'), name: newName || 'Copy', cards: [], _demo: true } };
  const url = teamId != null && teamId !== '' ? `/boards/${boardId}/duplicate?teamId=${encodeURIComponent(teamId)}` : `/boards/${boardId}/duplicate`;
  const response = await axiosInstance.post(url, { name: newName });
  return response.data;
};

// ============================================================================
// BOARD SHARE / PUBLIC
// ============================================================================

/**
 * Get current share status for a board
 */
export const getBoardShareStatus = async (boardId, teamId) => {
  const response = await axiosInstance.get(`/boards/${boardId}/share?teamId=${teamId}`);
  return response.data;
};

/**
 * Create or update public share link for a board
 */
export const createBoardShare = async (boardId, teamId, expiresAt = null) => {
  const body = { teamId };
  if (expiresAt) body.expiresAt = expiresAt;
  const response = await axiosInstance.post(`/boards/${boardId}/share`, body);
  return response.data;
};

/**
 * Revoke public share for a board
 */
export const revokeBoardShare = async (boardId, teamId) => {
  await axiosInstance.delete(`/boards/${boardId}/share?teamId=${teamId}`);
};

/**
 * Get board by public token (no auth required)
 */
export const getPublicBoard = async (token) => {
  const response = await axiosInstance.get(`/boards/public/${token}`);
  return response.data;
};

/**
 * Execute a card in the context of a public board (no auth required)
 */
export const executeCardPublic = async (publicToken, cardId) => {
  const response = await axiosInstance.post('/boards/execute/public', {
    publicToken,
    cardId
  });
  return response.data;
};

// ============================================================================
// CARDS API
// ============================================================================

/**
 * Fetch all cards for a board
 */
export const fetchCards = async (boardId, teamId = null) => {
  const url = teamId != null && teamId !== '' ? `/boards/${boardId}/cards?teamId=${encodeURIComponent(teamId)}` : `/boards/${boardId}/cards`;
  const response = await axiosInstance.get(url);
  return response.data;
};

/**
 * Create new card
 */
export const createCard = async (boardId, cardData, teamId = null) => {
  if (isDemoTeamActive()) return { success: true, card: { id: _demoId('card'), board_id: boardId, ...cardData, _demo: true } };
  const url = teamId != null && teamId !== '' ? `/boards/${boardId}/cards?teamId=${encodeURIComponent(teamId)}` : `/boards/${boardId}/cards`;
  const response = await axiosInstance.post(url, cardData);
  return response.data;
};

/**
 * Update card
 */
export const updateCard = async (boardId, cardId, updates, teamId = null) => {
  if (isDemoTeamActive()) return { success: true, card: { id: cardId, board_id: boardId, ...updates, _demo: true } };
  const url = teamId != null && teamId !== '' ? `/boards/${boardId}/cards/${cardId}?teamId=${encodeURIComponent(teamId)}` : `/boards/${boardId}/cards/${cardId}`;
  const response = await axiosInstance.put(url, updates);
  return response.data;
};

/**
 * Delete card
 */
export const deleteCard = async (boardId, cardId, teamId = null) => {
  if (isDemoTeamActive()) return { success: true };
  const url = teamId != null && teamId !== '' ? `/boards/${boardId}/cards/${cardId}?teamId=${encodeURIComponent(teamId)}` : `/boards/${boardId}/cards/${cardId}`;
  const response = await axiosInstance.delete(url);
  return response.data;
};

/**
 * Execute card query
 * options.boardTimeOverride: optional { mode, preset?, start?, end?, interval?, timezone? } for runtime-only time override
 */
export const executeCard = async (boardId, cardId, teamId = null, options = {}) => {
  const url = teamId != null && teamId !== '' ? `/boards/${boardId}/cards/${cardId}/execute?teamId=${encodeURIComponent(teamId)}` : `/boards/${boardId}/cards/${cardId}/execute`;
  const body = options.boardTimeOverride != null ? { boardTimeOverride: options.boardTimeOverride } : {};
  const response = await axiosInstance.post(url, body);
  return response.data;
};

/**
 * Update card positions (bulk update for drag & drop / block order)
 */
export const updateCardPositions = async (boardId, positions, teamId = null) => {
  if (isDemoTeamActive()) return { success: true };
  const url = teamId != null && teamId !== '' ? `/boards/${boardId}/cards/positions?teamId=${encodeURIComponent(teamId)}` : `/boards/${boardId}/cards/positions`;
  const response = await axiosInstance.put(url, { positions });
  return response.data;
};

/**
 * Upload image or video for a board block (multipart/form-data)
 * @param {string} boardId
 * @param {File} file
 * @returns {Promise<{ url: string, filename: string }>}
 */
export const uploadBoardMedia = async (boardId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post(`/boards/${boardId}/upload`, formData);
  return response.data?.data || response.data;
};

// ============================================================================
// HOME WORKSPACE (per user, per team)
// ============================================================================

/**
 * @param {boolean} [includeResults] — when true, server runs all analytic cards and returns results
 */
export const fetchHomeWorkspace = async (teamId, { includeResults = false } = {}) => {
  const q = new URLSearchParams();
  if (teamId != null && teamId !== '') q.append('teamId', String(teamId));
  if (includeResults) q.append('includeResults', 'true');
  const qs = q.toString();
  const response = await axiosInstance.get(`/home-workspace${qs ? `?${qs}` : ''}`);
  return response.data;
};

export const updateHomeWorkspace = async (updates, teamId = null) => {
  if (isDemoTeamActive()) return { success: true, ...updates };
  const url = teamId != null && teamId !== '' ? `/home-workspace?teamId=${encodeURIComponent(teamId)}` : '/home-workspace';
  const response = await axiosInstance.put(url, updates);
  return response.data;
};

export const executeHomeWorkspace = async (teamId, options = {}) => {
  const url = teamId != null && teamId !== '' ? `/home-workspace/execute?teamId=${encodeURIComponent(teamId)}` : '/home-workspace/execute';
  const body = options.boardTimeOverride != null ? { boardTimeOverride: options.boardTimeOverride } : {};
  const response = await axiosInstance.post(url, body);
  return response.data;
};

export const createHomeWorkspaceCard = async (cardData, teamId = null) => {
  if (isDemoTeamActive()) return { success: true, card: { id: _demoId('hwcard'), ...cardData, _demo: true } };
  const url = teamId != null && teamId !== '' ? `/home-workspace/cards?teamId=${encodeURIComponent(teamId)}` : '/home-workspace/cards';
  const response = await axiosInstance.post(url, cardData);
  return response.data;
};

export const updateHomeWorkspaceCard = async (cardId, updates, teamId = null) => {
  const url = teamId != null && teamId !== '' ? `/home-workspace/cards/${cardId}?teamId=${encodeURIComponent(teamId)}` : `/home-workspace/cards/${cardId}`;
  const response = await axiosInstance.put(url, updates);
  return response.data;
};

export const deleteHomeWorkspaceCard = async (cardId, teamId = null) => {
  const url = teamId != null && teamId !== '' ? `/home-workspace/cards/${cardId}?teamId=${encodeURIComponent(teamId)}` : `/home-workspace/cards/${cardId}`;
  const response = await axiosInstance.delete(url);
  return response.data;
};

export const updateHomeWorkspaceCardPositions = async (positions, teamId = null) => {
  const url = teamId != null && teamId !== '' ? `/home-workspace/cards/positions?teamId=${encodeURIComponent(teamId)}` : '/home-workspace/cards/positions';
  const response = await axiosInstance.put(url, { positions });
  return response.data;
};

export const executeHomeWorkspaceCard = async (cardId, teamId = null, options = {}) => {
  const url = teamId != null && teamId !== '' ? `/home-workspace/cards/${cardId}/execute?teamId=${encodeURIComponent(teamId)}` : `/home-workspace/cards/${cardId}/execute`;
  const body = options.boardTimeOverride != null ? { boardTimeOverride: options.boardTimeOverride } : {};
  const response = await axiosInstance.post(url, body);
  return response.data;
};

/**
 * @param {string|number} homeWorkspaceId
 * @param {File} file
 */
export const uploadHomeWorkspaceMedia = async (homeWorkspaceId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post(`/home-workspace/upload/${homeWorkspaceId}`, formData);
  return response.data?.data || response.data;
};

// ============================================================================
// INSIGHTS API
// ============================================================================

/**
 * Execute Query DSL
 */
export const executeInsights = async (queryDSL, teamId = null) => {
  const url = teamId ? `/insights/execute?teamId=${encodeURIComponent(teamId)}` : '/insights/execute';
  const response = await axiosInstance.post(url, queryDSL);
  return response.data;
};

/**
 * Paginated users/groups at a funnel step (builder drill-down). POST body matches backend funnel-step-users.
 */
export const fetchFunnelStepUsers = async ({
  queryDSL,
  step,
  offset = 0,
  limit = 25,
  breakdownValues,
  teamId = null,
  search,
  exportAll = false,
  listMode = 'converted',
}) => {
  const url = teamId
    ? `/insights/funnel-step-users?teamId=${encodeURIComponent(teamId)}`
    : '/insights/funnel-step-users';
  const body = {
    queryDSL,
    step,
    offset,
    limit,
  };
  if (breakdownValues != null && Array.isArray(breakdownValues)) {
    body.breakdownValues = breakdownValues;
  }
  if (search != null && String(search).trim() !== '') {
    body.search = String(search).trim();
  }
  if (exportAll === true) {
    body.exportAll = true;
  }
  if (listMode === 'dropoff') {
    body.listMode = 'dropoff';
  }
  const response = await axiosInstance.post(url, body);
  const payload = response.data?.data ?? response.data;
  return payload;
};

/**
 * Paginated users/groups for one insights metric datapoint (builder drill-down). POST body matches backend datapoint-users.
 */
export const fetchInsightDatapointUsers = async ({
  queryDSL,
  metricId,
  offset = 0,
  limit = 25,
  breakdownValues,
  teamId = null,
  search,
  exportAll = false,
  timeSlice = null,
}) => {
  const url = teamId
    ? `/insights/datapoint-users?teamId=${encodeURIComponent(teamId)}`
    : '/insights/datapoint-users';
  const body = {
    queryDSL,
    metricId,
    offset,
    limit,
  };
  if (breakdownValues != null && Array.isArray(breakdownValues)) {
    body.breakdownValues = breakdownValues;
  }
  if (search != null && String(search).trim() !== '') {
    body.search = String(search).trim();
  }
  if (exportAll === true) {
    body.exportAll = true;
  }
  if (timeSlice != null && timeSlice.start && timeSlice.end) {
    body.timeSlice = { start: timeSlice.start, end: timeSlice.end };
  }
  const response = await axiosInstance.post(url, body);
  const payload = response.data?.data ?? response.data;
  return payload;
};

/**
 * Execute a Feature Flow query DSL (queryType: 'flows').
 * Uses the same /insights/execute endpoint as insights and funnels.
 */
export const executeFlow = async (queryDSL, teamId = null, options = {}) => {
  const url = teamId ? `/insights/execute?teamId=${encodeURIComponent(teamId)}` : '/insights/execute';
  const { signal } = options;
  const response = await axiosInstance.post(url, queryDSL, signal ? { signal } : undefined);
  return response.data;
};

/**
 * Preview query results (limited to 10 rows)
 */
export const previewInsights = async (queryDSL) => {
  const response = await axiosInstance.post('/insights/preview', queryDSL);
  return response.data;
};

/**
 * Validate Query DSL
 */
export const validateQueryDSL = async (queryDSL) => {
  const response = await axiosInstance.post('/insights/validate', queryDSL);
  return response.data;
};

/**
 * Fetch available events
 */
export const fetchEvents = async (teamId = null) => {
  const params = teamId ? { teamId: teamId.toString() } : {};
  const queryString = new URLSearchParams(params).toString();
  const url = `/insights/events${queryString ? `?${queryString}` : ''}`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const fetchContractMethods = async () => ({ contractMethods: [] });

/**
 * Fetch properties for an event
 */
export const fetchEventProperties = async (eventName) => {
  const response = await axiosInstance.get(`/insights/properties?event=${encodeURIComponent(eventName)}`);
  return response.data;
};

/**
 * Fetch property values (for filter dropdowns)
 */
export const fetchPropertyValues = async (eventName, property, limit = 50) => {
  const response = await axiosInstance.get(
    `/insights/property-values?event=${encodeURIComponent(eventName)}&property=${encodeURIComponent(property)}&limit=${limit}`
  );
  return response.data;
};

/**
 * Fetch example Query DSLs
 */
export const fetchExamples = async () => {
  const response = await axiosInstance.get('/insights/examples');
  return response.data;
};

// ============================================================================
// QUERY DSL BUILDERS (Helper functions)
// ============================================================================

/**
 * Build a basic Insights Query DSL
 * Supports both legacy single-event format and new multi-event format
 */
export const buildInsightsQuery = ({
  // Legacy single-event format (for backward compatibility)
  event,
  aggregation = 'count',
  timeRange = { preset: '30D', interval: 'day', timezone: 'UTC' },
  filters = [],
  breakdown = null, // Legacy: single breakdown
  breakdowns = null, // New: array of breakdowns
  globalFilters = [],
  chartType = 'line',
  // New multi-event format
  metrics = null,
  // Breakdown display options (persisted so board shows same top N as editor)
  breakdownLimit = 10,
  breakdownSortOrder = 'average-desc',
  breakdownShowMax = false
}) => {
  // Normalize breakdowns: use breakdowns array if provided, otherwise convert single breakdown to array
  // Empty arrays are treated as null (no breakdown)
  const normalizedBreakdowns = breakdowns && Array.isArray(breakdowns) && breakdowns.length > 0
    ? breakdowns 
    : (breakdown ? [breakdown] : null);
  // If metrics array is provided, use new format
  if (metrics && Array.isArray(metrics) && metrics.length > 0) {
    // Filter out metrics with no events to prevent invalid queries
    const validMetrics = metrics.filter(metric => {
      const hasEvents = metric.events && Array.isArray(metric.events) && metric.events.length > 0;
      const hasEvent = metric.event && metric.event.trim() !== '';
      return hasEvents || hasEvent;
    });
    
    if (validMetrics.length === 0) {
      console.warn('[DEBUG] buildInsightsQuery: No valid metrics with events, returning null');
      return null;
    }
    
    return {
      version: 1,
      queryType: 'insights',
      time: {
        preset: timeRange.preset || null,
        start: timeRange.start || null,
        end: timeRange.end || null,
        interval: timeRange.interval || 'day',
        timezone: timeRange.timezone || 'UTC'
      },
      definition: {
        metrics: validMetrics.map((metric, index) => {
          const firstEventName =
            metric.events && metric.events.length > 0
              ? metric.events[0].name
              : metric.event || null;

          if (!firstEventName) {
            console.warn(`[DEBUG] buildInsightsQuery: Metric ${metric.id || index} has no event name`);
          }

          return {
            id: metric.id || `m${index + 1}`,
            // Custom display name (saved in board card); default is query-derived e.g. "Total Events of page_view"
            metricName: metric.metricName && String(metric.metricName).trim() ? String(metric.metricName).trim() : null,
            // Legacy single-event field so the validator stays happy
            event: firstEventName,
            aggregation: metric.aggregation || 'count',
            axis: metric.axis || 'left',
            // Uniques: accumulate by user (distinct_id) or by group (group_identities for accumulationGroupKey)
            countSubject: metric.countSubject || 'user',
            accumulationGroupKey:
              metric.countSubject === 'group' && metric.accumulationGroupKey
                ? String(metric.accumulationGroupKey).trim()
                : undefined,
            // New multi-event structure used by the compiler
            events: metric.events || [], // Array of { name, filters }
            // Metric-level filters (empty for now, filters are per-event)
            filters: []
          };
        }),
        breakdowns: normalizedBreakdowns, // Use breakdowns array
        // Only set breakdown for legacy compatibility if breakdowns is null/empty
        breakdown: normalizedBreakdowns && normalizedBreakdowns.length > 0 ? null : (breakdown || null),
        filters: Array.isArray(globalFilters) ? globalFilters : [],
        breakdownLimit: normalizedBreakdowns && normalizedBreakdowns.length > 0 ? breakdownLimit : undefined,
        breakdownSortOrder: normalizedBreakdowns && normalizedBreakdowns.length > 0 ? breakdownSortOrder : undefined,
        breakdownShowMax: normalizedBreakdowns && normalizedBreakdowns.length > 0 ? breakdownShowMax : undefined
      },
      visualization: {
        chartType,
        config: {}
      }
    };
  }

  // Legacy format: single event
  return {
    version: 1,
    queryType: 'insights',
    time: {
      preset: timeRange.preset || null,
      start: timeRange.start || null,
      end: timeRange.end || null,
      interval: timeRange.interval || 'day',
      timezone: timeRange.timezone || 'UTC'
    },
    definition: {
      metrics: [
        {
          id: 'm1',
          metricName: null,
          event,
          aggregation,
          filters: filters || [] // Metric-scoped filters
        }
      ],
      breakdowns: normalizedBreakdowns, // Use breakdowns array
      // Only set breakdown for legacy compatibility if breakdowns is null/empty
      breakdown: normalizedBreakdowns && normalizedBreakdowns.length > 0 ? null : (breakdown || null),
      filters: Array.isArray(globalFilters) ? globalFilters : [],
      breakdownLimit: normalizedBreakdowns && normalizedBreakdowns.length > 0 ? breakdownLimit : undefined,
      breakdownSortOrder: normalizedBreakdowns && normalizedBreakdowns.length > 0 ? breakdownSortOrder : undefined,
      breakdownShowMax: normalizedBreakdowns && normalizedBreakdowns.length > 0 ? breakdownShowMax : undefined
    },
    visualization: {
      chartType,
      config: {}
    }
  };
};

function normalizeGlobalFilters(globalFilters = []) {
  return (Array.isArray(globalFilters) ? globalFilters : [])
    .filter((f) => f.propertySource !== 'transaction' && f.category !== 'transaction_properties')
    .map((f) => {
    const fieldOrProperty = f.field ?? f.property;
    const propertyType = f.propertyType || (fieldOrProperty && String(fieldOrProperty).startsWith('user.') ? inferUserPropertyType(fieldOrProperty) : null);
    let value = f.value;
    let value2 = f.value2;
    if (propertyType === 'number' && value != null && value !== '') {
      const n = Number(value);
      if (!Number.isNaN(n)) value = n;
    }
    if (propertyType === 'number' && value2 != null && value2 !== '') {
      const n2 = Number(value2);
      if (!Number.isNaN(n2)) value2 = n2;
    }
    return {
      field: fieldOrProperty,
      property: fieldOrProperty,
      operator: f.operator,
      value,
      value2,
      propertyType: f.propertyType || propertyType,
      propertySource: f.propertySource,
      category: f.category,
      listMode: f.listMode,
      listItemType: f.listItemType,
    };
  });
}

function inferUserPropertyType(property) {
  if (!property || typeof property !== 'string') return null;
  const numericUserProps = ['user.total_page_views', 'user.total_time_spent', 'total_page_views', 'total_time_spent'];
  return numericUserProps.includes(property) ? 'number' : null;
}

/**
 * Single entry: build the correct Query DSL based on report type (dynamic selection).
 * Use this for both execute and save so time/filters are built in one place.
 * @param {'insight' | 'funnel' | 'retention'} reportType
 * @param {object} state - timeRange, chartType, globalFilters, breakdowns; plus metrics/funnelSteps/retention fields
 * @returns {object|null} queryDSL or null if invalid
 */
export const buildReportQuery = (reportType, state) => {
  const timeRange = state.timeRange || { preset: '30D', interval: 'day', timezone: 'UTC' };
  const globalFilters = normalizeGlobalFilters(state.globalFilters);
  const chartType = state.chartType || (reportType === 'funnel' ? 'funnel-steps' : 'line');
  const breakdowns = state.breakdowns;

  if (reportType === 'funnel') {
    const steps = state.funnelSteps || state.steps || [];
    const measuring = state.measuring || 'conversion_rate';
    const measuringProperty = state.measuringProperty || null;
    const measuringAggregation = state.measuringAggregation || 'average';
    const measuringPercentileValue = state.measuringPercentileValue || 50;
    const conversionWindow = state.conversionWindow || { value: 7, unit: 'days' };
    const anyOrder = state.anyOrder === true;
    return buildFunnelQuery({ 
      timeRange, 
      steps, 
      globalFilters, 
      breakdowns, 
      chartType, 
      measuring, 
      measuringProperty, 
      measuringAggregation, 
      measuringPercentileValue, 
      conversionWindow,
      anyOrder,
      countSubject: state.countSubject,
      accumulationGroupKey: state.accumulationGroupKey,
      breakdownLimit: state.breakdownLimit,
      breakdownSortOrder: state.breakdownSortOrder,
      breakdownShowMax: state.breakdownShowMax
    });
  }

  if (reportType === 'retention') {
    const startEvent = state.startEvent || '';
    const returnEvent = state.returnEvent || '';
    const retentionRule = state.retentionRule || 'on_or_after';
    const metric = state.metric || 'retention_rate';
    const breakdown = state.breakdown || null;
    return buildRetentionQuery({
      startEvent,
      returnEvent,
      startEventSource: state.startEventSource || 'event',
      returnEventSource: state.returnEventSource || 'event',
      retentionRule,
      metric,
      timeRange,
      globalFilters,
      breakdown,
      chartType,
      countSubject: state.countSubject,
      accumulationGroupKey: state.accumulationGroupKey,
    });
  }

  return buildInsightsQuery({
    timeRange,
    chartType,
    globalFilters,
    breakdowns: breakdowns ?? state.breakdown ?? null,
    breakdownLimit: state.breakdownLimit,
    breakdownSortOrder: state.breakdownSortOrder,
    breakdownShowMax: state.breakdownShowMax,
    metrics: state.metrics,
    event: state.event,
    aggregation: state.aggregation,
    filters: state.filters,
  });
};

/**
 * Build a Funnel Query DSL (same time/filter/breakdown shape as insights; definition has steps).
 */
export const buildFunnelQuery = ({
  timeRange = { preset: '30D', interval: 'day', timezone: 'UTC' },
  steps = [], // [{ id, eventName, label?, source?: 'event'|'transaction' }]
  globalFilters = [],
  breakdowns = null,
  chartType = 'funnel-steps',
  measuring = 'conversion_rate', // Measuring type: 'conversion_rate', 'unique_users', 'total_conversions', 'total_sessions', 'time_to_convert'
  measuringProperty = null, // Legacy: was used for removed property-sum measuring
  measuringAggregation = 'average', // For time_to_convert: 'average', 'median', 'percentile', 'minimum', 'maximum'
  measuringPercentileValue = 50, // For percentile aggregation (0-100)
  conversionWindow = { value: 7, unit: 'days' }, // Conversion window: { value: number, unit: 'seconds'|'minutes'|'hours'|'days'|'weeks'|'months'|'session' }
  anyOrder = false, // When true, steps may complete in any sequence within the conversion window
  /** Same semantics as insight metrics: 'user' | 'group' with accumulationGroupKey when group */
  countSubject = 'user',
  accumulationGroupKey = null,
  // Breakdown display options (same as insights)
  breakdownLimit = 10,
  breakdownSortOrder = 'average-desc',
  breakdownShowMax = false
}) => {
  if (!steps || steps.length === 0) return null;
  const validSteps = steps.filter(s => (s.eventName || s.entityName || '').trim() !== '');
  if (validSteps.length === 0) return null;
  return {
    version: 1,
    queryType: 'funnels',
    time: {
      preset: timeRange.preset || null,
      start: timeRange.start || null,
      end: timeRange.end || null,
      interval: timeRange.interval || 'day',
      timezone: timeRange.timezone || 'UTC',
    },
    definition: {
      steps: validSteps.map((s, i) => ({
        id: s.id || `step-${i + 1}`,
        event: (s.eventName || s.entityName || '').trim(),
        eventName: (s.eventName || s.entityName || '').trim(),
        entityName: (s.entityName || s.eventName || '').trim(),
        entityKind: s.entityKind || 'event',
        ...(s.spanKind ? { spanKind: s.spanKind } : {}),
        label: (s.label || s.eventName || s.entityName || '').trim() || null,
        source: s.source || 'event',
        filters: Array.isArray(s.filters) ? s.filters : [],
      })),
      filters: Array.isArray(globalFilters) ? globalFilters : [],
      breakdowns: breakdowns && Array.isArray(breakdowns) && breakdowns.length > 0 ? breakdowns : null,
      measuring: measuring || 'conversion_rate',
      measuringProperty: measuringProperty || null,
      measuringAggregation: measuring === 'time_to_convert' ? (measuringAggregation || 'average') : null,
      measuringPercentileValue: measuring === 'time_to_convert' && measuringAggregation === 'percentile' ? (measuringPercentileValue || 50) : null,
      conversionWindow: conversionWindow || { value: 7, unit: 'days' },
      ...(anyOrder ? { anyOrder: true } : {}),
      ...(countSubject === 'group' && accumulationGroupKey && String(accumulationGroupKey).trim() !== ''
        ? { countSubject: 'group', accumulationGroupKey: String(accumulationGroupKey).trim() }
        : { countSubject: 'user' }),
      // Breakdown display options (same as insights)
      breakdownLimit: breakdowns && breakdowns.length > 0 ? breakdownLimit : undefined,
      breakdownSortOrder: breakdowns && breakdowns.length > 0 ? breakdownSortOrder : undefined,
      breakdownShowMax: breakdowns && breakdowns.length > 0 ? breakdownShowMax : undefined,
    },
    visualization: { chartType: chartType || 'funnel-steps', config: {} },
  };
};

/**
 * Build a filter object
 */
export const buildFilter = ({
  type = 'include',
  property,
  operator = 'equals',
  value
}) => {
  return {
    type,
    property,
    operator,
    value
  };
};

/**
 * Build a breakdown object
 */
export const buildBreakdown = (property, limit = 10) => {
  return {
    property,
    limit
  };
};

/**
 * Build a Retention Query DSL
 */
export const buildRetentionQuery = ({
  startEvent,
  returnEvent,
  startEventSource = 'event',
  returnEventSource = 'event',
  retentionRule = 'on_or_after',
  metric = 'retention_rate',
  timeRange = { preset: '30D', interval: 'day', timezone: 'UTC' },
  globalFilters = [],
  breakdown = null,
  chartType = 'line',
  /** Same semantics as funnel/flow: 'user' | 'group' with accumulationGroupKey when group */
  countSubject = 'user',
  accumulationGroupKey = null,
}) => {
  // startEvent / returnEvent may be a string (legacy / event entityKind)
  // or an object { entityName, entityKind, spanKind } for agent/span.
  const startName = typeof startEvent === 'object' && startEvent
    ? (startEvent.entityName || '')
    : String(startEvent || '');
  const returnName = typeof returnEvent === 'object' && returnEvent
    ? (returnEvent.entityName || '')
    : String(returnEvent || '');
  if (!startName.trim() || !returnName.trim()) return null;
  const startObj = (typeof startEvent === 'object' && startEvent && startEvent.entityKind && startEvent.entityKind !== 'event')
    ? { entityName: startName.trim(), entityKind: startEvent.entityKind, ...(startEvent.spanKind ? { spanKind: startEvent.spanKind } : {}) }
    : startName.trim();
  const returnObj = (typeof returnEvent === 'object' && returnEvent && returnEvent.entityKind && returnEvent.entityKind !== 'event')
    ? { entityName: returnName.trim(), entityKind: returnEvent.entityKind, ...(returnEvent.spanKind ? { spanKind: returnEvent.spanKind } : {}) }
    : returnName.trim();

  // Ensure time configuration is valid: must have either preset or both start and end
  const hasCustomRange = timeRange.start && timeRange.end;
  const hasPreset = timeRange.preset && String(timeRange.preset).trim() !== '';
  const validPreset = hasPreset ? timeRange.preset : (hasCustomRange ? null : '30D');

  return {
    version: 1,
    queryType: 'retention',
    time: {
      preset: validPreset,
      start: hasCustomRange ? timeRange.start : null,
      end: hasCustomRange ? timeRange.end : null,
      interval: timeRange.interval || 'day',
      timezone: timeRange.timezone || 'UTC'
    },
    definition: {
      startEvent: startObj,
      returnEvent: returnObj,
      startEventSource: startEventSource || 'event',
      returnEventSource: returnEventSource || 'event',
      retentionRule: retentionRule || 'on_or_after',
      metric: metric || 'retention_rate',
      ...(countSubject === 'group' && accumulationGroupKey && String(accumulationGroupKey).trim() !== ''
        ? { countSubject: 'group', accumulationGroupKey: String(accumulationGroupKey).trim() }
        : { countSubject: 'user' }),
      filters: normalizeGlobalFilters(globalFilters),
      breakdown: breakdown && breakdown.property ? {
        property: breakdown.property,
        propertyLabel: breakdown.propertyLabel,
        propertySource: breakdown.propertySource || 'event',
        // Retention breakdown controls
        step: breakdown.step === 2 ? 2 : 1,
        limit: typeof breakdown.limit === 'number' ? breakdown.limit : 50,
        showMax: breakdown.showMax === true,
        sortOrder: breakdown.sortOrder || 'value-desc',
        // Optional: allow turning off the "Other" bucket; defaults on for chart parity
        includeOther: breakdown.includeOther !== false,
      } : null
    },
    visualization: {
      chartType: chartType || 'line',
      config: {}
    }
  };
};

// ============================================================================
// ERROR HANDLING UTILITIES
// ============================================================================

/**
 * True if the error indicates the request was aborted, cancelled, or a transaction aborted (no need to show a harsh error).
 */
export const isAbortedOrCancelled = (error) => {
  const msg = (error?.message || error?.response?.data?.message || error?.response?.data?.error || '').toLowerCase();
  return (
    error?.code === 'ECONNABORTED' ||
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    msg.includes('aborted') ||
    msg.includes('cannot be fulfilled') ||
    msg.includes('request was cancelled')
  );
};

/**
 * Extract error message from API response. Uses friendly text for aborted/cancelled requests.
 */
export const getErrorMessage = (error) => {
  if (isAbortedOrCancelled(error)) {
    return 'Request was cancelled or timed out. Please try again.';
  }
  if (Array.isArray(error.response?.data?.errors) && error.response.data.errors.length > 0) {
    return error.response.data.errors.join(', ');
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error) => {
  return error.isNetworkError || !error.response;
};

export default {
  // Boards
  fetchBoards,
  fetchBoard,
  createBoard,
  updateBoard,
  deleteBoard,
  duplicateBoard,
  
  // Cards
  fetchCards,
  createCard,
  updateCard,
  deleteCard,
  executeCard,
  updateCardPositions,
  
  // Insights / Flow
  executeInsights,
  fetchFunnelStepUsers,
  fetchInsightDatapointUsers,
  executeFlow,
  previewInsights,
  validateQueryDSL,
  fetchEvents,
  fetchEventProperties,
  fetchPropertyValues,
  fetchExamples,
  
  // Builders
  buildInsightsQuery,
  buildFunnelQuery,
  buildRetentionQuery,
  buildReportQuery,
  buildFilter,
  buildBreakdown,
  
  // Utilities
  getErrorMessage,
  isNetworkError,
  isAbortedOrCancelled,
};
